<template>
  <div class="coi-integrations">
    <div class="page-container">
      <CoiPageHeader
        title="Integrations"
        subtitle="FranConnect sync and franchise location reporting"
        icon="fas fa-plug"
      >
        <template #actions>
          <router-link to="/clients" class="btn-secondary">
            <i class="fas fa-home"></i> Home
          </router-link>
        </template>
      </CoiPageHeader>

      <div v-if="loading" class="loading-row">
        <i class="fas fa-spinner fa-spin"></i> Loading integrations...
      </div>

      <div v-else-if="!hubEnabled" class="empty-card">
        <i class="fas fa-store-slash"></i>
        <h3>No FranConnect integration configured</h3>
        <p v-if="canManage">
          Connect a client's FranConnect environment to a compliance project to open Franchise Data —
          sync monitoring, active locations report, and FC gap reconciliation.
        </p>
        <p v-else>
          Your organization does not have FranConnect connected yet. Contact your ADI platform administrator
          to enable franchise sync for your compliance project.
        </p>
        <router-link v-if="canManage" to="/client-management" class="btn-primary">
          Client Management
        </router-link>
      </div>

      <template v-else>
        <p class="section-desc">
          Select a franchise data workspace. Each opens sync status, manual sync, active locations report,
          and FC-vs-CO-Verify gap review.
        </p>

        <div class="integration-grid">
          <article
            v-for="project in projects"
            :key="project.project_id"
            class="integration-card"
          >
            <header class="card-header">
              <div>
                <h3>{{ project.project_name }}</h3>
                <p class="card-org">{{ project.organization_name }}</p>
              </div>
              <span class="env-badge">{{ project.environment || 'FranConnect' }}</span>
            </header>

            <router-link :to="hubPath(project.project_id)" class="card-footer">
              <span class="open-label">
                <i class="fas fa-store"></i> Open Franchise Data
              </span>
              <i class="fas fa-arrow-right"></i>
            </router-link>

            <div class="card-links">
              <router-link :to="hubPath(project.project_id, 'overview')">Sync overview</router-link>
              <router-link :to="hubPath(project.project_id, 'locations')">Active locations</router-link>
              <router-link :to="hubPath(project.project_id, 'reconciliation')">FC gap</router-link>
              <router-link :to="hubPath(project.project_id, 'issues')">Sync issues</router-link>
            </div>
          </article>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CoiPageHeader from '@/components/coi/CoiPageHeader.vue'
import auth from '@/services/auth'
import franchiseHubContext from '@/services/franchiseHubContext'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const hubEnabled = ref(false)
const projects = ref([])

const canManage = computed(() => auth.hasPlatformAccess() || auth.hasCrossOrgComplianceAccess())

function hubPath(projectId, tab = null) {
  const tabQuery = tab || route.query.tab
  return {
    path: `/projects/${projectId}/franchise-data`,
    query: tabQuery ? { tab: tabQuery } : {},
  }
}

async function loadAndRoute() {
  loading.value = true
  try {
    const ctx = await franchiseHubContext.load(true)
    hubEnabled.value = Boolean(ctx.hub_enabled)
    projects.value = ctx.projects || []

    if (hubEnabled.value && projects.value.length === 1) {
      const pid = projects.value[0].project_id
      await router.replace(hubPath(pid))
    }
  } finally {
    loading.value = false
  }
}

onMounted(loadAndRoute)
</script>

<style scoped>
.coi-integrations {
  min-height: 100vh;
  background: #f1f5f9;
  padding: 1.5rem 1rem 3rem;
}

.page-container {
  max-width: 960px;
  margin: 0 auto;
}

.btn-secondary,
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  text-decoration: none;
}

.btn-secondary {
  background: white;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.btn-primary {
  background: #1e40af;
  color: white;
  margin-top: 1rem;
}

.section-desc {
  margin: 0 0 1.25rem;
  color: #64748b;
  font-size: 0.9rem;
}

.integration-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.integration-card {
  display: block;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.integration-card:hover {
  border-color: #1e40af;
  box-shadow: 0 4px 14px rgba(30, 64, 175, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.card-header h3 {
  margin: 0;
  font-size: 1.05rem;
  color: #1e293b;
}

.card-org {
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.env-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #eff6ff;
  color: #1e40af;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
  text-decoration: none;
  color: inherit;
}

.open-label {
  font-weight: 700;
  color: #1e40af;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.card-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-top: 0.75rem;
}

.card-links a {
  font-size: 0.8rem;
  color: #64748b;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.card-links a:hover {
  color: #1e40af;
}

.empty-card {
  text-align: center;
  padding: 2.5rem 1.5rem;
  background: white;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #64748b;
}

.empty-card i {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  color: #94a3b8;
}

.empty-card h3 {
  margin: 0 0 0.5rem;
  color: #334155;
}

.loading-row {
  text-align: center;
  padding: 3rem;
  color: #64748b;
}
</style>
